import { useEffect, useRef, useState, type RefObject } from 'react';
import type { ArtifactRef, DownloadTicket, Project } from '@aimo/contracts/web';
import { safeTicketUrl, type ApiClient } from '../../api.js';
import { projectPreviewRef } from '../../stitch-landing.js';
import { Icon } from '../icon.js';
import { cn } from '../utils.js';

const previews = new Map<string, { url: string; expiresAt: number }>();
const pending = new Map<string, Promise<string>>();

/** A preview ticket is shared by every row showing the same artifact version until shortly before it expires. */
function previewUrl(api: ApiClient, ref: ArtifactRef): Promise<string> {
  const key = `${ref.artifactId}@${ref.version}`, cached = previews.get(key);
  if (cached && cached.expiresAt > Date.now() + 5000) return Promise.resolve(cached.url);
  let request = pending.get(key);
  if (!request) {
    request = api.request<DownloadTicket>('createDownloadTicket', { params: { id: ref.artifactId }, body: { version: ref.version, purpose: 'preview' } })
      .then(ticket => { const url = safeTicketUrl(ticket.downloadUrl); previews.set(key, { url, expiresAt: Date.parse(ticket.expiresAt) || 0 }); return url; })
      .finally(() => pending.delete(key));
    pending.set(key, request);
  }
  return request;
}

/** The project's result image URL, loaded once its element scrolls into view; null while absent, loading or broken. */
export function useProjectPreview<T extends HTMLElement>(project: Project, api: ApiClient | null): { tile: RefObject<T | null>; url: string | null; fail(): void } {
  const ref = projectPreviewRef(project);
  const tile = useRef<T>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const refKey = ref ? `${ref.artifactId}@${ref.version}` : '';

  useEffect(() => {
    setUrl(null); setFailed(false);
    const element = tile.current;
    if (!ref || !api || !element) return;
    let cancelled = false;
    const load = () => { previewUrl(api, ref).then(next => { if (!cancelled) setUrl(next); }, () => { if (!cancelled) setFailed(true); }); };
    if (typeof IntersectionObserver === 'undefined') { load(); return () => { cancelled = true; }; }
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect(); load();
    }, { rootMargin: '120px' });
    observer.observe(element);
    return () => { cancelled = true; observer.disconnect(); };
  }, [api, refKey]); // The ref object is rebuilt each render; its artifact version is the identity.

  return { tile, url: failed ? null : url, fail: () => setFailed(true) };
}

/** A small library thumbnail: the result image, or a quiet neutral tile until the project has one. */
export function ProjectThumbnail({ project, api, className }: { project: Project; api: ApiClient | null; className?: string }) {
  const { tile, url, fail } = useProjectPreview<HTMLDivElement>(project, api);
  return (
    <div ref={tile} aria-hidden className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-(--md-sys-color-surface-container-high) text-(--md-sys-color-outline)', className)}>
      {url ? <img src={url} alt="" decoding="async" onError={fail} className="size-full object-cover" /> : <Icon name="image" size={18} />}
    </div>
  );
}
